<template>
  <modal
    @close_modal="close"
    :title="$t('models_and_train_configs.train_log_title')"
  >
    <template v-slot:content>
      <div v-if="errorLogText" class="mb-4" data-testid="train-job-error-log">
        <div class="text-caption text--secondary mb-1">
          {{ $t("models_and_train_configs.train_log_error_title") }}
        </div>
        <pre class="train-log-raw train-log-raw--error">{{ errorLogText }}</pre>
      </div>

      <div v-if="trainLogFilename" class="mb-4">
        <div class="text-caption text--secondary mb-1">
          {{ $t("models_and_train_configs.train_log_raw_title") }}
        </div>
        <div v-if="rawLogLoading" class="text-body-2 text--secondary">
          {{ $t("models_and_train_configs.train_log_raw_loading") }}
        </div>
        <div v-else-if="rawLogError" class="text-body-2 error--text">
          {{ rawLogError }}
        </div>
        <pre v-else class="train-log-raw">{{ rawLogText }}</pre>
      </div>

      <div
        v-if="!hasMetrics && !trainLogFilename && !errorLogText"
        class="text--secondary"
      >
        {{ $t("models_and_train_configs.train_log_empty") }}
      </div>
      <template v-if="hasMetrics">
        <div class="mb-3 text-body-2">{{ stateLabel }}</div>
        <v-progress-linear
          v-if="job.progress > 0"
          :value="job.progress"
          height="8"
          rounded
          class="mb-4"
        />

        <div v-if="trainLossValues.length" class="mb-4 train-log-chart">
          <div class="text-caption text--secondary mb-1">
            {{ $t("models_and_train_configs.train_log_train_loss") }}
          </div>
          <v-sparkline
            :model-value="trainLossValues"
            color="primary"
            line-width="2"
            height="56"
            :width="chartWidth"
            padding="8"
            smooth
            fill
          />
        </div>

        <div v-if="valLossValues.length" class="mb-4 train-log-chart">
          <div class="text-caption text--secondary mb-1">
            {{ $t("models_and_train_configs.train_log_val_loss") }}
          </div>
          <v-sparkline
            :model-value="valLossValues"
            color="orange"
            line-width="2"
            height="56"
            :width="chartWidth"
            padding="8"
            smooth
            fill
          />
        </div>

        <div v-if="accuracyValues.length" class="mb-4 train-log-chart">
          <div class="text-caption text--secondary mb-1">
            {{ $t("models_and_train_configs.train_log_accuracy") }}
          </div>
          <v-sparkline
            :model-value="accuracyValues"
            :min="accuracyMin"
            :max="accuracyMax"
            color="success"
            line-width="2"
            height="56"
            :width="chartWidth"
            padding="8"
            smooth
            fill
          />
        </div>

        <div v-if="mapValues.length" class="mb-4 train-log-chart">
          <div class="text-caption text--secondary mb-1">
            {{ $t("models_and_train_configs.train_log_map") }}
          </div>
          <v-sparkline
            :model-value="mapValues"
            color="teal"
            line-width="2"
            height="56"
            :width="chartWidth"
            padding="8"
            smooth
            fill
          />
        </div>

        <v-simple-table v-if="epochRows.length" dense>
          <thead>
            <tr>
              <th>{{ $t("models_and_train_configs.train_log_epoch") }}</th>
              <th v-if="showTrainLossCol">
                {{ $t("models_and_train_configs.train_log_train_loss") }}
              </th>
              <th v-if="showValLossCol">
                {{ $t("models_and_train_configs.train_log_val_loss") }}
              </th>
              <th v-if="showAccuracyCol">
                {{ $t("models_and_train_configs.train_log_accuracy") }}
              </th>
              <th v-if="showMapCol">
                {{ $t("models_and_train_configs.train_log_map") }}
              </th>
              <th v-if="showMinutesCol">
                {{ $t("models_and_train_configs.train_log_minutes") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in epochRows" :key="row.epoch">
              <td>{{ row.epoch }}</td>
              <td v-if="showTrainLossCol">{{ formatNum(row.train_loss) }}</td>
              <td v-if="showValLossCol">{{ formatNum(row.val_loss) }}</td>
              <td v-if="showAccuracyCol">{{ formatPct(row.accuracy) }}</td>
              <td v-if="showMapCol">{{ formatNum(row.map) }}</td>
              <td v-if="showMinutesCol">{{ formatMinutes(row.minutes) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </template>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import { getTrainLogFilenameForModel } from "@/utils/trainMetrics";

export default {
  name: "TrainLogModal",
  components: { Modal },
  props: {
    job: { required: true },
    model: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      chartWidth: 400,
      rawLogLoading: false,
      rawLogText: "",
      rawLogError: null,
    };
  },
  computed: {
    trainLogFilename() {
      return this.model ? getTrainLogFilenameForModel(this.model) : null;
    },
    errorLogText() {
      const logs = this.job && this.job.logs;
      if (!logs || !String(logs).trim()) {
        return null;
      }
      return String(logs);
    },
    description() {
      try {
        return JSON.parse(this.job.description);
      } catch {
        return {};
      }
    },
    metrics() {
      return this.description.train_metrics || {};
    },
    hasMetrics() {
      const m = this.metrics;
      return (
        m.current_epoch != null ||
        (m.train_losses && m.train_losses.length) ||
        (m.val_losses && m.val_losses.length) ||
        (m.accuracy && m.accuracy.length) ||
        (m.map50_95_series && m.map50_95_series.length) ||
        (m.map50_series && m.map50_series.length)
      );
    },
    stateLabel() {
      const m = this.metrics;
      if (String(this.description.state || "").toLowerCase() === "failed") {
        return this.$t("models_and_train_configs.train_failed");
      }
      if (this.job.processed === 1) {
        if (this.description.state) {
          return this.description.state;
        }
        if (m.current_epoch != null && m.total_epochs) {
          return `${this.$t("models_and_train_configs.train_log_completed")} · ${m.current_epoch}/${m.total_epochs}`;
        }
        return this.$t("models_and_train_configs.train_log_completed");
      }
      if (this.description.state) {
        return this.description.state;
      }
      if (m.current_epoch != null && m.total_epochs) {
        return `Epoch ${m.current_epoch}/${m.total_epochs}`;
      }
      return this.$t("models_and_train_configs.train_log_in_progress");
    },
    trainLossValues() {
      return this.numericSeries(this.metrics.train_losses);
    },
    valLossValues() {
      return this.numericSeries(this.metrics.val_losses);
    },
    accuracyValues() {
      return this.numericSeries(this.metrics.accuracy);
    },
    accuracyMin() {
      const values = this.accuracyValues;
      if (!values.length) {
        return 0;
      }
      const min = Math.min(...values);
      const max = Math.max(...values);
      if (max - min < 0.05) {
        return Math.max(0, min - 0.05);
      }
      return min;
    },
    accuracyMax() {
      const values = this.accuracyValues;
      if (!values.length) {
        return 1;
      }
      const min = Math.min(...values);
      const max = Math.max(...values);
      if (max - min < 0.05) {
        return Math.min(1, max + 0.05);
      }
      return max;
    },
    mapValues() {
      return this.numericSeries(
        this.metrics.map50_95_series || this.metrics.map50_series,
      );
    },
    minutesValues() {
      return this.numericSeries(this.metrics.epoch_minutes);
    },
    showTrainLossCol() {
      return this.trainLossValues.length > 0;
    },
    showValLossCol() {
      return this.valLossValues.length > 0;
    },
    showAccuracyCol() {
      return this.accuracyValues.length > 0;
    },
    showMapCol() {
      return this.mapValues.length > 0;
    },
    showMinutesCol() {
      return this.minutesValues.length > 0;
    },
    epochRows() {
      const m = this.metrics;
      const n = Math.max(
        this.trainLossValues.length,
        this.valLossValues.length,
        this.accuracyValues.length,
        this.mapValues.length,
        this.minutesValues.length,
        0,
      );
      const mapSeries = m.map50_95_series || m.map50_series || [];
      const rows = [];
      for (let i = 0; i < n; i++) {
        rows.push({
          epoch: i + 1,
          train_loss: m.train_losses ? m.train_losses[i] : null,
          val_loss: m.val_losses ? m.val_losses[i] : null,
          accuracy: m.accuracy ? m.accuracy[i] : null,
          map: mapSeries[i] != null ? mapSeries[i] : null,
          minutes: m.epoch_minutes ? m.epoch_minutes[i] : null,
        });
      }
      return rows;
    },
  },
  watch: {
    trainLogFilename: {
      immediate: true,
      handler() {
        this.loadRawTrainLog();
      },
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    numericSeries(values) {
      if (!values || !values.length) {
        return [];
      }
      return values.map((v) => Number(v));
    },
    formatNum(v) {
      if (v == null || Number.isNaN(Number(v))) {
        return "—";
      }
      return Number(v).toFixed(4);
    },
    formatPct(v) {
      if (v == null || Number.isNaN(Number(v))) {
        return "—";
      }
      return `${(Number(v) * 100).toFixed(1)}%`;
    },
    formatMinutes(v) {
      if (v == null || Number.isNaN(Number(v))) {
        return "—";
      }
      const n = Number(v);
      if (Number.isInteger(n)) {
        return String(n);
      }
      return n.toFixed(1);
    },
    async loadRawTrainLog() {
      const name = this.trainLogFilename;
      if (!name) {
        this.rawLogLoading = false;
        this.rawLogText = "";
        this.rawLogError = null;
        return;
      }
      this.rawLogLoading = true;
      this.rawLogError = null;
      this.rawLogText = "";
      const projectId =
        this.$store.getters["user/get_current_project_id"] ||
        sessionStorage.getItem("project_id");
      const token = localStorage.getItem("token");
      if (!projectId || !token) {
        this.rawLogError = this.$t(
          "models_and_train_configs.train_log_raw_error",
        );
        this.rawLogLoading = false;
        return;
      }
      try {
        const url = `/api/protected/models/${encodeURIComponent(projectId)}/${encodeURIComponent(name)}?token=${encodeURIComponent(token)}`;
        const res = await fetch(url);
        if (!res.ok) {
          this.rawLogError = this.$t(
            "models_and_train_configs.train_log_raw_error",
          );
          return;
        }
        const text = await res.text();
        const maxChars = 512 * 1024;
        if (text.length > maxChars) {
          const start = text.length - maxChars;
          this.rawLogText =
            "…\n" +
            text.slice(start) +
            "\n\n" +
            this.$t("models_and_train_configs.train_log_raw_truncated");
        } else {
          this.rawLogText = text;
        }
      } catch {
        this.rawLogError = this.$t(
          "models_and_train_configs.train_log_raw_error",
        );
      } finally {
        this.rawLogLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.train-log-chart {
  width: 100%;
  max-width: 100%;
}
.train-log-chart :deep(svg) {
  width: 100%;
  display: block;
}
.train-log-raw {
  max-height: min(50vh, 420px);
  overflow: auto;
  margin: 0;
  padding: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
}
.train-log-raw--error {
  background: rgba(var(--v-theme-error), 0.08);
  color: rgb(var(--v-theme-error));
}
</style>
